import React, { useCallback, useRef } from 'react'

import { UploadContext, type UploadContextValue } from './uploadContext'

/**
 * Upload Provider Component
 *
 * Wrap your app with this provider to enable background uploads.
 * Must be placed at app root level, above Router.
 */
export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const fileReferences = useRef(new Map<string, File>())
  const activeUploads = useRef(new Map<string, XMLHttpRequest>())

  const registerFile = useCallback((fileId: string, file: File) => {
    fileReferences.current.set(fileId, file)
  }, [])

  const registerUpload = useCallback((fileId: string, xhr: XMLHttpRequest) => {
    activeUploads.current.set(fileId, xhr)
  }, [])

  const unregisterUpload = useCallback((fileId: string) => {
    activeUploads.current.delete(fileId)
  }, [])

  const clearFileReferences = useCallback(() => {
    // Cancel all active uploads before clearing
    activeUploads.current.forEach((xhr) => {
      try {
        xhr.abort()
      } catch {
        // Ignore abort errors
      }
    })
    activeUploads.current.clear()
    fileReferences.current.clear()
  }, [])

  const getFile = useCallback((fileId: string): File | undefined => {
    return fileReferences.current.get(fileId)
  }, [])

  const value: UploadContextValue = React.useMemo(
    () => ({
      fileReferences,
      activeUploads,
      registerFile,
      registerUpload,
      unregisterUpload,
      clearFileReferences,
      getFile,
    }),
    [
      fileReferences,
      activeUploads,
      registerFile,
      registerUpload,
      unregisterUpload,
      clearFileReferences,
      getFile,
    ]
  )

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  )
}
